/** Reads from Supabase, mapped into app types. Writes go through ./sync.ts (offline queue). */
import { supabase } from './supabase.ts';
import type { Json } from './database.types.ts';
import { parseStepsState, type StepsState } from '../core/lesson-plan.ts';
import type { CardState } from '../core/fsrs.ts';
import type { Lesson } from '../core/content.ts';
import { validateLesson } from '../core/validate.ts';
import { addDays, appDay, dayStartIso } from '../core/date.ts';
import { allAvatarPaths, avatarFormat, avatarPath, staleAvatarPath } from '../core/avatar.ts';

export interface Course {
  id: string;
  lang: string;
  title: string;
  title_ru: string;
  exam: string | null;
  sort: number;
}

export interface Book {
  id: string;
  course_id: string;
  title: string;
  sort: number;
}

export interface LessonMeta {
  id: string;
  book_id: string;
  number: number;
  title: string;
  title_ru: string;
  key_char: string | null;
  version: number;
  /** Cache key for the content: changes whenever the row is rewritten (version + updated_at). */
  revision: string;
  /** False for published metadata rows whose content has not been imported yet. */
  available: boolean;
}

/** A type alias (not an interface) so it is assignable to the jsonb `Json` type. */
export type ProfileSettings = {
  eyeColor?: string;
};

export interface Profile {
  id: string;
  display_name: string;
  daily_goal_xp: number;
  active_course_id: string | null;
  settings: ProfileSettings;
  /** Public URL of the uploaded photo, or null for the robo-cat. */
  avatar_url: string | null;
}

export interface ProgressRecord {
  lesson_id: string;
  status: 'started' | 'done';
  steps: StepsState;
  best_accuracy: number | null;
  completed_at: string | null;
}

export interface CardFront {
  text: string;
  pinyin?: string;
  sentence?: string;
  sentencePinyin?: string;
}

export interface CardBack {
  ru: string;
  pos?: string;
  sentenceRu?: string;
}

export type CardKind = 'word' | 'sentence' | 'grammar' | 'mistake';

export interface Card {
  id: string;
  kind: CardKind;
  ref_id: string;
  lang: string;
  front: CardFront;
  back: CardBack;
  due: Date;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  reps: number;
  lapses: number;
  state: CardState;
  last_review: Date | null;
  suspended: boolean;
  created_at: Date;
}

export interface LeagueRow {
  user_id: string;
  display_name: string;
  xp: number;
  is_me: boolean;
  avatar_url: string | null;
  eye_color: string | null;
  /** Languages the member studies (course langs), e.g. ['en', 'zh']. */
  langs: string[];
  /** Weeks this member finished first. */
  wins: number;
}

export interface XpEventRow {
  day: string;
  amount: number;
}

/** Days of XP history loaded for streaks; longer streaks are shown as at least this long. */
export const XP_HISTORY_DAYS = 400;

/**
 * Unwraps a Supabase response. The SDK returns a discriminated union
 * (`{ data: Row; error: null } | { data: null; error: PostgrestError }`), so
 * `T` is inferred as `Row | null` and narrowed back with `NonNullable`.
 */
function check<T>(result: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (result.error) throw new Error(result.error.message);
  if (result.data === null || result.data === undefined) throw new Error('Пустой ответ сервера');
  return result.data as NonNullable<T>;
}

/** PostgREST caps responses (1000 rows by default); page until a short page arrives. */
const PAGE_SIZE = 1000;

async function fetchAll<T>(
  page: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const chunk = check(await page(from, from + PAGE_SIZE - 1));
    rows.push(...chunk);
    if (chunk.length < PAGE_SIZE) return rows;
  }
}

const asObject = (value: Json): Record<string, Json | undefined> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};

function parseSettings(value: Json): ProfileSettings {
  const raw = asObject(value);
  return typeof raw.eyeColor === 'string' ? { eyeColor: raw.eyeColor } : {};
}

export async function fetchProfile(userId: string): Promise<Profile> {
  const row = check(
    await supabase
      .from('profiles')
      .select('id, display_name, daily_goal_xp, active_course_id, settings, avatar_url')
      .eq('id', userId)
      .single(),
  );
  return { ...row, settings: parseSettings(row.settings) };
}

export interface ProfilePatch {
  display_name?: string;
  daily_goal_xp?: number;
  active_course_id?: string;
  settings?: ProfileSettings;
  avatar_url?: string | null;
}

export async function updateProfile(userId: string, patch: ProfilePatch): Promise<void> {
  const { error } = await supabase.from('profiles').update(patch).eq('id', userId);
  if (error) throw new Error(error.message);
}

export async function fetchCourses(): Promise<Course[]> {
  return check(
    await supabase.from('courses').select('id, lang, title, title_ru, exam, sort').order('sort'),
  );
}

/** Books of every course: a handful of rows, so course switching needs no request. */
export async function fetchBooks(): Promise<Book[]> {
  return check(await supabase.from('books').select('id, course_id, title, sort').order('sort'));
}

interface LessonMetaRow extends Omit<LessonMeta, 'available' | 'revision'> {
  updated_at: string;
  schema_version: string | null;
}

const lessonRevision = (row: { version: number; updated_at: string }) => `${row.version}@${row.updated_at}`;

/**
 * Metadata of every published lesson (RLS hides the rest). `schemaVersion` is read from the
 * content to tell imported lessons from «скоро» placeholders; the database does this in
 * ~5 ms for all rows, far below one network round trip.
 */
export async function fetchLessonMetas(): Promise<LessonMeta[]> {
  const rows = check(
    await supabase
      .from('lessons')
      .select('id, book_id, number, title, title_ru, key_char, version, updated_at, schema_version:content->>schemaVersion')
      .order('number')
      .returns<LessonMetaRow[]>(),
  );
  return rows.map(
    ({ schema_version, updated_at, ...meta }): LessonMeta => ({
      ...meta,
      revision: lessonRevision({ version: meta.version, updated_at }),
      available: schema_version !== null,
    }),
  );
}

export interface LessonContent {
  id: string;
  revision: string;
  lesson: Lesson;
}

export async function fetchLessonContent(id: string): Promise<LessonContent> {
  const row = check(
    await supabase.from('lessons').select('id, version, updated_at, content').eq('id', id).single(),
  );
  const issues = validateLesson(row.content);
  if (issues.length > 0) {
    const first = issues[0]!;
    throw new Error(`Урок повреждён (${first.path}: ${first.message})`);
  }
  return { id: row.id, revision: lessonRevision(row), lesson: row.content as unknown as Lesson };
}

export async function fetchProgress(): Promise<ProgressRecord[]> {
  const rows = check(
    await supabase.from('lesson_progress').select('lesson_id, status, steps, best_accuracy, completed_at'),
  );
  return rows.map(
    (r): ProgressRecord => ({
      lesson_id: r.lesson_id,
      status: r.status === 'done' ? 'done' : 'started',
      steps: parseStepsState(r.steps),
      best_accuracy: r.best_accuracy,
      completed_at: r.completed_at,
    }),
  );
}

export async function fetchXpEvents(now: Date): Promise<XpEventRow[]> {
  const since = addDays(appDay(now), -XP_HISTORY_DAYS);
  return fetchAll((from, to) =>
    supabase.from('xp_events').select('day, amount').gte('day', since).order('id').range(from, to),
  );
}

const CARD_COLUMNS =
  'id, kind, ref_id, lang, front, back, due, stability, difficulty, elapsed_days, scheduled_days, reps, lapses, state, last_review, suspended, created_at';

export async function fetchCards(): Promise<Card[]> {
  const rows = await fetchAll((from, to) =>
    supabase.from('cards').select(CARD_COLUMNS).order('created_at').order('id').range(from, to),
  );
  return rows.map((r): Card => {
    const front = asObject(r.front);
    const back = asObject(r.back);
    const str = (v: Json | undefined) => (typeof v === 'string' ? v : undefined);
    return {
      id: r.id,
      kind: r.kind as CardKind,
      ref_id: r.ref_id,
      lang: r.lang,
      front: {
        text: str(front.text) ?? '',
        ...(str(front.pinyin) ? { pinyin: str(front.pinyin)! } : {}),
        ...(str(front.sentence) ? { sentence: str(front.sentence)! } : {}),
        ...(str(front.sentencePinyin) ? { sentencePinyin: str(front.sentencePinyin)! } : {}),
      },
      back: {
        ru: str(back.ru) ?? '',
        ...(str(back.pos) ? { pos: str(back.pos)! } : {}),
        ...(str(back.sentenceRu) ? { sentenceRu: str(back.sentenceRu)! } : {}),
      },
      due: new Date(r.due),
      stability: r.stability,
      difficulty: r.difficulty,
      elapsed_days: r.elapsed_days,
      scheduled_days: r.scheduled_days,
      reps: r.reps,
      lapses: r.lapses,
      state: r.state as CardState,
      last_review: r.last_review ? new Date(r.last_review) : null,
      suspended: r.suspended,
      created_at: new Date(r.created_at),
    };
  });
}

/** New cards introduced today per language (first reviews of cards in state New). */
/**
 * Cards first reviewed today (in state New), as ids. Counted per language against the cards
 * by `countNewSeen`, so this request can run alongside the card download.
 */
export async function fetchNewSeenTodayIds(now: Date): Promise<string[]> {
  const rows = await fetchAll((from, to) =>
    supabase
      .from('review_log')
      .select('card_id')
      .eq('state', 0)
      .gte('reviewed_at', dayStartIso(appDay(now)))
      .order('id')
      .range(from, to),
  );
  return rows.map((r) => r.card_id);
}

export function countNewSeen(cardIds: readonly string[], cards: readonly Card[]): Record<string, number> {
  const langById = new Map(cards.map((c) => [c.id, c.lang]));
  const counts: Record<string, number> = {};
  for (const id of cardIds) {
    const lang = langById.get(id);
    if (lang) counts[lang] = (counts[lang] ?? 0) + 1;
  }
  return counts;
}

export async function fetchLeague(): Promise<LeagueRow[]> {
  const rows = check(await supabase.rpc('league_week_v2'));
  // xp is a bigint sum in SQL; normalise in case it arrives as a string.
  return rows.map(
    (r): LeagueRow => ({
      user_id: r.user_id,
      display_name: r.display_name,
      xp: Number(r.xp),
      is_me: r.is_me,
      avatar_url: r.avatar_url,
      eye_color: r.eye_color,
      langs: Array.isArray(r.langs) ? r.langs : [],
      wins: Number(r.wins) || 0,
    }),
  );
}

export interface MemberLanguage {
  lang: string;
  course_title: string;
  level: string;
  book_title: string;
  lesson_number: number;
  lessons_done: number;
}

export interface EarnedAchievement {
  code: string;
  /** '' for one-off badges, the week's Monday for league wins. */
  period: string;
  earned_at: string;
}

export interface LeagueMember {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  eye_color: string | null;
  languages: MemberLanguage[];
  /** Latest placement-test level per language (empty until the member takes a test). */
  placements: { lang: string; level: string }[];
  achievements: EarnedAchievement[];
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v) || 0);

function parseAchievement(v: unknown): EarnedAchievement | null {
  if (!isRecord(v) || typeof v.code !== 'string') return null;
  return { code: v.code, period: str(v.period), earned_at: str(v.earned_at) };
}

/** One member's public card (languages, levels, badges) for the league popover. */
export async function fetchLeagueMember(userId: string): Promise<LeagueMember | null> {
  const raw: unknown = check(await supabase.rpc('league_member', { p_user: userId }));
  if (!isRecord(raw)) return null;
  const languages = Array.isArray(raw.languages) ? raw.languages : [];
  const achievements = Array.isArray(raw.achievements) ? raw.achievements : [];
  return {
    user_id: str(raw.user_id),
    display_name: str(raw.display_name),
    avatar_url: typeof raw.avatar_url === 'string' ? raw.avatar_url : null,
    eye_color: typeof raw.eye_color === 'string' ? raw.eye_color : null,
    languages: languages.filter(isRecord).map((l) => ({
      lang: str(l.lang),
      course_title: str(l.course_title),
      level: str(l.level),
      book_title: str(l.book_title),
      lesson_number: num(l.lesson_number),
      lessons_done: num(l.lessons_done),
    })),
    placements: (Array.isArray(raw.placements) ? raw.placements : [])
      .filter(isRecord)
      .map((p) => ({ lang: str(p.lang), level: str(p.level) }))
      .filter((p) => p.lang && p.level),
    achievements: achievements.map(parseAchievement).filter((a): a is EarnedAchievement => a !== null),
  };
}

/** Lets the server award any badges the caller has earned; returns only the new ones. */
export async function checkAchievements(): Promise<EarnedAchievement[]> {
  const { data, error } = await supabase.rpc('check_achievements');
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({ code: r.code, period: r.period, earned_at: r.earned_at }));
}

export async function fetchMyAchievements(): Promise<EarnedAchievement[]> {
  const rows = check(await supabase.from('user_achievements').select('code, period, earned_at').order('earned_at'));
  return rows;
}

export const AVATAR_BUCKET = 'avatars';

/**
 * Uploads the (already resized) avatar to the learner's own folder with its real format
 * (WebP, or JPEG on Safari), removes the file of the other format and returns its public URL.
 */
export async function uploadAvatar(userId: string, image: Blob): Promise<string> {
  const format = avatarFormat(image.type);
  const path = avatarPath(userId, format);
  const { error } = await supabase.storage
    .from(AVATAR_BUCKET)
    .upload(path, image, { upsert: true, contentType: format.contentType, cacheControl: '3600' });
  if (error) throw new Error(error.message);
  await supabase.storage.from(AVATAR_BUCKET).remove([staleAvatarPath(userId, format)]);
  const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path);
  // A version query busts caches after a re-upload; the DB check allows exactly this shape.
  return `${data.publicUrl}?v=${Date.now()}`;
}

export async function removeAvatar(userId: string): Promise<void> {
  await supabase.storage.from(AVATAR_BUCKET).remove(allAvatarPaths(userId));
}
